/**
 * Predictive Search for BulkRubberBands V2
 * Debounced fetch from Shopify's /search/suggest endpoint.
 * Keyboard navigation (arrows, enter, escape).
 */
(function () {
  'use strict';

  const DEBOUNCE_MS = 250;
  const MIN_CHARS = 2;

  const searchOverlay = document.querySelector('[data-predictive-search]');
  const searchInput = document.querySelector('[data-predictive-search-input]');
  const resultsContainer = document.querySelector('[data-predictive-search-results]');
  const loadingEl = document.querySelector('[data-predictive-search-loading]');

  if (!searchOverlay || !searchInput || !resultsContainer) return;

  let debounceTimer = null;
  let currentQuery = '';
  let selectedIndex = -1;
  let resultItems = [];

  // Open search
  function openSearch() {
    searchOverlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setTimeout(function () { searchInput.focus(); }, 100);
  }

  // Close search
  function closeSearch() {
    searchOverlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    searchInput.value = '';
    resultsContainer.innerHTML = '';
    loadingEl.style.display = 'none';
    selectedIndex = -1;
    resultItems = [];
  }

  // Bind open triggers
  document.querySelectorAll('[data-search-open]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      openSearch();
    });
  });

  // Bind close triggers
  document.querySelectorAll('[data-search-close]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      closeSearch();
    });
  });

  // Escape key closes search
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && searchOverlay.getAttribute('aria-hidden') === 'false') {
      closeSearch();
    }
  });

  // Debounced input handler
  searchInput.addEventListener('input', function () {
    var query = searchInput.value.trim();

    clearTimeout(debounceTimer);

    if (query.length < MIN_CHARS) {
      resultsContainer.innerHTML = '';
      loadingEl.style.display = 'none';
      currentQuery = '';
      return;
    }

    loadingEl.style.display = '';
    currentQuery = query;

    debounceTimer = setTimeout(function () {
      fetchResults(query);
    }, DEBOUNCE_MS);
  });

  // Fetch results from Shopify
  function fetchResults(query) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(query)
      + '&resources[type]=product,collection,page,article,query'
      + '&resources[limit]=6'
      + '&resources[options][unavailable_products]=hide';

    fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (query !== currentQuery) return;
        renderResults(data.resources.results, query);
      })
      .catch(function () {
        loadingEl.style.display = 'none';
      });
  }

  // Render results
  function renderResults(results, query) {
    loadingEl.style.display = 'none';
    selectedIndex = -1;

    var html = '';
    var hasResults = false;

    // Query suggestions
    if (results.queries && results.queries.length > 0) {
      hasResults = true;
      html += '<div class="predictive-search__group">';
      html += '<div class="predictive-search__group-heading">Suggestions</div>';
      results.queries.forEach(function (q) {
        html += '<a href="/search?q=' + encodeURIComponent(q.text) + '" class="predictive-search__suggestion" role="option" data-search-result>' + (q.styled_text || escapeHtml(q.text)) + '</a>';
      });
      html += '</div>';
    }

    // Products
    if (results.products && results.products.length > 0) {
      hasResults = true;
      html += '<div class="predictive-search__group">';
      html += '<div class="predictive-search__group-heading">Products</div>';
      results.products.forEach(function (p) {
        var imgHtml = '';
        if (p.image) {
          imgHtml = '<div class="predictive-search__product-image"><img src="' + getSizedImage(p.image, 112) + '" alt="' + escapeHtml(p.title) + '" width="56" height="56" loading="lazy"></div>';
        }
        var priceHtml = p.price ? '<div class="predictive-search__product-price">' + formatMoney(p.price) + '</div>' : '';

        html += '<a href="' + p.url + '" class="predictive-search__product" role="option" data-search-result>'
          + imgHtml
          + '<div class="predictive-search__product-info">'
          + '<div class="predictive-search__product-title">' + escapeHtml(p.title) + '</div>'
          + priceHtml
          + '</div></a>';
      });
      html += '</div>';
    }

    // Collections
    if (results.collections && results.collections.length > 0) {
      hasResults = true;
      html += '<div class="predictive-search__group">';
      html += '<div class="predictive-search__group-heading">Collections</div>';
      results.collections.forEach(function (c) {
        html += '<a href="' + c.url + '" class="predictive-search__link" role="option" data-search-result>' + escapeHtml(c.title) + '</a>';
      });
      html += '</div>';
    }

    // Pages
    if (results.pages && results.pages.length > 0) {
      hasResults = true;
      html += '<div class="predictive-search__group">';
      html += '<div class="predictive-search__group-heading">Pages</div>';
      results.pages.forEach(function (pg) {
        html += '<a href="' + pg.url + '" class="predictive-search__link" role="option" data-search-result>' + escapeHtml(pg.title) + '</a>';
      });
      html += '</div>';
    }

    // Articles
    if (results.articles && results.articles.length > 0) {
      hasResults = true;
      html += '<div class="predictive-search__group">';
      html += '<div class="predictive-search__group-heading">Articles</div>';
      results.articles.forEach(function (a) {
        html += '<a href="' + a.url + '" class="predictive-search__link" role="option" data-search-result>' + escapeHtml(a.title) + '</a>';
      });
      html += '</div>';
    }

    if (!hasResults) {
      html = '<div class="predictive-search__no-results">No results found for "' + escapeHtml(query) + '"</div>';
    }

    // "Search for" button
    html += '<a href="/search?q=' + encodeURIComponent(query) + '" class="predictive-search__search-for" data-search-result>Search for "' + escapeHtml(query) + '"</a>';

    resultsContainer.innerHTML = html;
    resultItems = resultsContainer.querySelectorAll('[data-search-result]');
  }

  // Keyboard navigation
  searchInput.addEventListener('keydown', function (e) {
    if (resultItems.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      selectedIndex = Math.min(selectedIndex + 1, resultItems.length - 1);
      updateSelection();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      selectedIndex = Math.max(selectedIndex - 1, -1);
      updateSelection();
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      resultItems[selectedIndex].click();
    }
  });

  function updateSelection() {
    resultItems.forEach(function (item, i) {
      item.setAttribute('aria-selected', i === selectedIndex ? 'true' : 'false');
    });
    if (selectedIndex >= 0) {
      resultItems[selectedIndex].scrollIntoView({ block: 'nearest' });
    }
  }

  // Helpers
  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function getSizedImage(url, size) {
    if (!url) return '';
    // Shopify image URL resizing
    if (url.indexOf('cdn.shopify.com') > -1) {
      return url.replace(/(\.[a-z]+)(\?|$)/, '_' + size + 'x$1$2');
    }
    return url;
  }

  function formatMoney(cents) {
    if (typeof cents === 'string') cents = parseInt(cents, 10);
    return '$' + (cents / 100).toFixed(2);
  }
})();
